// src/pages/Partner/PartnerInvitations.jsx

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Clock3,
  Mail,
  RefreshCw,
  RotateCw,
  Send,
  UserCheck,
  UserX,
} from "lucide-react";

import {
  Link,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";

import InviteLearnerModal from "../../components/partner/InviteLearnerModal";

import {
  fetchPartnerCohorts,
} from "../../lib/partner/partnerCohorts";

import {
  fetchPartnerInvitations,
  revokePartnerInvitation,
  sendPartnerCohortInvite,
} from "../../lib/partner/partnerInvitations";


function formatDate(value) {
  if (!value) {
    return "Not available";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value;
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}


function statusClasses(status) {
  switch (status) {
    case "accepted":
      return "bg-green-100 text-green-800";

    case "pending":
      return "bg-amber-100 text-amber-800";

    case "expired":
      return "bg-gray-200 text-gray-700";

    case "revoked":
      return "bg-red-100 text-red-700";

    default:
      return "bg-gray-100 text-gray-700";
  }
}


export default function PartnerInvitations() {
  const {
    access,
  } = useOutletContext();


  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();


  const selectedCohortId =
    searchParams.get("cohort") ||
    "";


  const [
    cohorts,
    setCohorts,
  ] = useState([]);


  const [
    invitations,
    setInvitations,
  ] = useState([]);


  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all");


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    error,
    setError,
  ] = useState("");


  const [
    message,
    setMessage,
  ] = useState("");


  const [
    inviteModalOpen,
    setInviteModalOpen,
  ] = useState(false);


  const [
    changingInvitationId,
    setChangingInvitationId,
  ] = useState("");


  const selectedCohort =
    useMemo(
      () =>
        cohorts.find(
          (cohort) =>
            String(
              cohort.id
            ) ===
            String(
              selectedCohortId
            )
        ) || null,
      [
        cohorts,
        selectedCohortId,
      ]
    );


  const manageableCohorts =
    useMemo(
      () =>
        cohorts.filter(
          (cohort) =>
            cohort.can_manage &&
            [
              "draft",
              "active",
            ].includes(
              cohort.status
            )
        ),
      [cohorts]
    );


  const loadData =
    useCallback(
      async () => {
        try {
          setLoading(true);
          setError("");

          const [
            cohortRows,
            invitationRows,
          ] =
            await Promise.all([
              fetchPartnerCohorts(),

              fetchPartnerInvitations(
                selectedCohortId ||
                  null
              ),
            ]);

          setCohorts(
            Array.isArray(
              cohortRows
            )
              ? cohortRows
              : []
          );

          setInvitations(
            Array.isArray(
              invitationRows
            )
              ? invitationRows
              : []
          );
        } catch (
          loadError
        ) {
          console.error(
            "Partner invitations loading failed:",
            loadError
          );

          setError(
            loadError?.message ||
              "The invitations could not be loaded."
          );
        } finally {
          setLoading(false);
        }
      },
      [
        selectedCohortId,
      ]
    );


  useEffect(() => {
    loadData();
  }, [
    loadData,
  ]);


  const filteredInvitations =
    useMemo(
      () => {
        if (
          statusFilter ===
          "all"
        ) {
          return invitations;
        }

        return invitations.filter(
          (invitation) =>
            invitation.status ===
            statusFilter
        );
      },
      [
        invitations,
        statusFilter,
      ]
    );


  const summary =
    useMemo(
      () => {
        return invitations.reduce(
          (
            totals,
            invitation
          ) => {
            if (
              Object.prototype
                .hasOwnProperty.call(
                  totals,
                  invitation.status
                )
            ) {
              totals[
                invitation.status
              ] += 1;
            }

            return totals;
          },
          {
            pending: 0,
            accepted: 0,
            expired: 0,
            revoked: 0,
          }
        );
      },
      [invitations]
    );


  function handleCohortChange(
    event
  ) {
    const nextCohortId =
      event.target.value;

    const nextParams =
      new URLSearchParams(
        searchParams
      );

    if (nextCohortId) {
      nextParams.set(
        "cohort",
        nextCohortId
      );
    } else {
      nextParams.delete(
        "cohort"
      );
    }

    setSearchParams(
      nextParams
    );
  }


  async function handleInvited(
    result
  ) {
    setError("");

    setMessage(
      `Invitation sent to ${result.email} for ${result.cohortName}.`
    );

    await loadData();
  }


  async function handleResend(
    invitation
  ) {
    const confirmed =
      window.confirm(
        `Send a fresh invitation to ${invitation.email}? The previous invitation link will stop working.`
      );

    if (!confirmed) {
      return;
    }


    /*
     * Entrepreneur name became a required
     * field for cohort invitations.
     *
     * Older invitation records may predate
     * the invitee_name column/value.
     *
     * We deliberately do not fake a name
     * using the email address.
     */
    const inviteeName =
      String(
        invitation
          ?.invitee_name ||
          ""
      ).trim();


    if (!inviteeName) {
      setMessage("");

      setError(
        "This invitation does not have an entrepreneur name saved. Please revoke it and send a new invitation instead."
      );

      return;
    }


    try {
      setChangingInvitationId(
        invitation
          .invitation_id
      );

      setError("");
      setMessage("");


      await sendPartnerCohortInvite({
        cohortId:
          invitation
            .cohort_id,

        name:
          inviteeName,

        email:
          invitation
            .email,

        expiresInDays:
          7,
      });


      setMessage(
        `A fresh invitation was sent to ${invitation.email}.`
      );


      await loadData();
    } catch (
      resendError
    ) {
      console.error(
        "Resending invitation failed:",
        resendError
      );

      setError(
        resendError?.message ||
          "The invitation could not be resent."
      );
    } finally {
      setChangingInvitationId(
        ""
      );
    }
  }


  async function handleRevoke(
    invitation
  ) {
    const confirmed =
      window.confirm(
        `Revoke the invitation for ${invitation.email}? Their current invitation link will stop working.`
      );

    if (!confirmed) {
      return;
    }


    try {
      setChangingInvitationId(
        invitation
          .invitation_id
      );

      setError("");
      setMessage("");


      await revokePartnerInvitation(
        invitation
          .invitation_id
      );


      setMessage(
        `The invitation for ${invitation.email} was revoked.`
      );


      await loadData();
    } catch (
      revokeError
    ) {
      console.error(
        "Revoking invitation failed:",
        revokeError
      );

      setError(
        revokeError?.message ||
          "The invitation could not be revoked."
      );
    } finally {
      setChangingInvitationId(
        ""
      );
    }
  }


  const mayInvite =
    manageableCohorts.length >
    0;


  return (
    <>
      {/* ==================================================================== */}
      {/* Header                                                               */}
      {/* ==================================================================== */}

      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">

        <div>
          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
            Partner · Invitations
          </p>

          <h1 className="mt-2 text-[34px] font-black tracking-[-0.03em] md:text-[42px]">
            Learner invitations
          </h1>

          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575] md:text-base">
            Invite new or existing Gwin
            learners into cohorts managed
            by {access.partner_name}.
          </p>
        </div>


        {mayInvite && (
          <button
            type="button"
            onClick={() =>
              setInviteModalOpen(
                true
              )
            }
            className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F]"
          >
            <Send className="mr-2 h-4 w-4" />

            Invite learner
          </button>
        )}

      </header>


      {/* ==================================================================== */}
      {/* Summary                                                              */}
      {/* ==================================================================== */}

      <section className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">

        <SummaryCard
          label="Pending"
          value={
            summary.pending
          }
          icon={
            Clock3
          }
        />


        <SummaryCard
          label="Accepted"
          value={
            summary.accepted
          }
          icon={
            UserCheck
          }
        />


        <SummaryCard
          label="Expired"
          value={
            summary.expired
          }
          icon={
            RefreshCw
          }
        />


        <SummaryCard
          label="Revoked"
          value={
            summary.revoked
          }
          icon={
            UserX
          }
        />

      </section>


      {/* ==================================================================== */}
      {/* Filters                                                              */}
      {/* ==================================================================== */}

      <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">

        <div className="grid gap-4 md:grid-cols-2">

          <div>
            <label
              htmlFor="invitation-cohort-filter"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Cohort
            </label>


            <select
              id="invitation-cohort-filter"
              value={
                selectedCohortId
              }
              onChange={
                handleCohortChange
              }
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="">
                All accessible cohorts
              </option>


              {cohorts.map(
                (cohort) => (
                  <option
                    key={
                      cohort.id
                    }
                    value={
                      cohort.id
                    }
                  >
                    {cohort.name}
                  </option>
                )
              )}

            </select>
          </div>


          <div>
            <label
              htmlFor="invitation-status-filter"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Status
            </label>


            <select
              id="invitation-status-filter"
              value={
                statusFilter
              }
              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target
                    .value
                )
              }
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="all">
                All statuses
              </option>

              <option value="pending">
                Pending
              </option>

              <option value="accepted">
                Accepted
              </option>

              <option value="expired">
                Expired
              </option>

              <option value="revoked">
                Revoked
              </option>

            </select>
          </div>

        </div>

      </section>


      {/* ==================================================================== */}
      {/* Messages                                                             */}
      {/* ==================================================================== */}

      {message && (
        <div
          role="status"
          className="mt-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800"
        >
          {message}
        </div>
      )}


      {error && (
        <div
          role="alert"
          className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700"
        >
          <span>
            {error}
          </span>


          <button
            type="button"
            onClick={
              loadData
            }
            className="inline-flex flex-none items-center rounded-full border border-red-300 px-3 py-2"
          >
            <RefreshCw className="mr-2 h-4 w-4" />

            Retry
          </button>

        </div>
      )}


      {/* ==================================================================== */}
      {/* Invitations                                                          */}
      {/* ==================================================================== */}

      {loading ? (

        <div className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-8 text-center text-sm font-semibold text-[#757575]">
          Loading invitations…
        </div>

      ) : filteredInvitations.length ===
        0 ? (

        <section className="mt-6 rounded-[28px] border border-dashed border-[#FFB98B] bg-white p-8 text-center shadow-sm">

          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
            <Mail className="h-7 w-7" />
          </div>


          <h2 className="mt-5 text-xl font-black">
            No invitations found
          </h2>


          <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-[#757575]">
            {selectedCohort
              ? `No matching invitation records were found for ${selectedCohort.name}.`
              : "No matching invitation records were found for your accessible cohorts."}
          </p>


          {mayInvite && (
            <button
              type="button"
              onClick={() =>
                setInviteModalOpen(
                  true
                )
              }
              className="mt-6 inline-flex items-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white"
            >
              <Send className="mr-2 h-4 w-4" />

              Send first invitation
            </button>
          )}

        </section>

      ) : (

        <section className="mt-6 grid gap-4 lg:grid-cols-2">

          {filteredInvitations.map(
            (invitation) => {
              const isChanging =
                changingInvitationId ===
                invitation
                  .invitation_id;


              return (
                <article
                  key={
                    invitation
                      .invitation_id
                  }
                  className="rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm"
                >

                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

                    <div className="min-w-0">

                      <span
                        className={[
                          "inline-flex rounded-full px-3 py-1 text-xs font-black capitalize",

                          statusClasses(
                            invitation
                              .status
                          ),
                        ].join(
                          " "
                        )}
                      >
                        {
                          invitation
                            .status
                        }
                      </span>


                      {invitation
                        .invitee_name && (
                        <p className="mt-4 text-base font-black text-[#333333]">
                          {
                            invitation
                              .invitee_name
                          }
                        </p>
                      )}


                      <h2
                        className={[
                          "break-all font-black",

                          invitation
                            .invitee_name
                            ? "mt-1 text-sm text-[#757575]"
                            : "mt-4 text-lg",
                        ].join(
                          " "
                        )}
                      >
                        {
                          invitation.email
                        }
                      </h2>


                      <Link
                        to={`/partner/cohorts/${invitation.cohort_id}`}
                        className="mt-2 inline-flex text-sm font-black text-[#FF914D] hover:text-[#E9782F]"
                      >
                        {
                          invitation
                            .cohort_name
                        }
                      </Link>

                    </div>


                    <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                      <Mail className="h-5 w-5" />
                    </div>

                  </div>


                  <dl className="mt-5 space-y-3 border-t border-[#EFEFEF] pt-4 text-sm">

                    <DetailRow
                      label="Invited"
                      value={
                        formatDate(
                          invitation
                            .invited_at
                        )
                      }
                    />


                    <DetailRow
                      label="Expires"
                      value={
                        formatDate(
                          invitation
                            .expires_at
                        )
                      }
                    />


                    <DetailRow
                      label="Invited by"
                      value={
                        invitation
                          .invited_by_email ||
                        "Partner team member"
                      }
                    />


                    {invitation
                      .accepted_at && (
                      <DetailRow
                        label="Accepted"
                        value={
                          formatDate(
                            invitation
                              .accepted_at
                          )
                        }
                      />
                    )}

                  </dl>


                  {invitation
                    .can_manage &&
                    invitation.status ===
                      "pending" && (

                    <div className="mt-5 flex flex-wrap gap-3">

                      <button
                        type="button"
                        onClick={() =>
                          handleResend(
                            invitation
                          )
                        }
                        disabled={
                          isChanging
                        }
                        className="inline-flex items-center rounded-full border border-[#FF914D] px-4 py-2.5 text-sm font-black text-[#FF914D] transition hover:bg-[#FFF4EC] disabled:opacity-50"
                      >
                        <RotateCw className="mr-2 h-4 w-4" />

                        {isChanging
                          ? "Updating…"
                          : "Resend"}
                      </button>


                      <button
                        type="button"
                        onClick={() =>
                          handleRevoke(
                            invitation
                          )
                        }
                        disabled={
                          isChanging
                        }
                        className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-black text-red-700 transition hover:bg-red-100 disabled:opacity-50"
                      >
                        <UserX className="mr-2 h-4 w-4" />

                        Revoke
                      </button>

                    </div>
                  )}

                </article>
              );
            }
          )}

        </section>
      )}


      {/* ==================================================================== */}
      {/* Invite modal                                                         */}
      {/* ==================================================================== */}

      <InviteLearnerModal
        open={
          inviteModalOpen
        }
        cohorts={
          manageableCohorts
        }
        initialCohortId={
          selectedCohort
            ?.can_manage
            ? selectedCohort.id
            : ""
        }
        onClose={() =>
          setInviteModalOpen(
            false
          )
        }
        onInvited={
          handleInvited
        }
      />

    </>
  );
}


function SummaryCard({
  label,
  value,
  icon: Icon,
}) {
  return (
    <article className="rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">

      <div className="flex items-start justify-between gap-4">

        <div>
          <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
            {label}
          </p>

          <p className="mt-3 text-3xl font-black">
            {value}
          </p>
        </div>


        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>

      </div>

    </article>
  );
}


function DetailRow({
  label,
  value,
}) {
  return (
    <div className="flex items-start justify-between gap-4">

      <dt className="text-[#757575]">
        {label}
      </dt>


      <dd className="max-w-[65%] break-words text-right font-black">
        {value}
      </dd>

    </div>
  );
}